import Link from 'next/link';
import { cn } from '@/lib/utils';
import { RADII, TOUCH } from './tokens';

type Variant = 'primary' | 'secondary' | 'ghost' | 'inverted';
type Size = 'md' | 'lg';

interface BaseProps {
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  ariaLabel?: string;
  fullWidth?: boolean;
}

interface ButtonProps extends BaseProps {
  href?: undefined;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}

interface LinkProps extends BaseProps {
  href: string;
  external?: boolean;
  /** Optional analytics hook — fired on click, navigation still proceeds via href. */
  onClick?: () => void;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  // Handlungs-Akzent in Fast-Schwarz (2026-07-31): hebt CTAs über
  // Helligkeit statt über eine zweite Buntfarbe vom blauen Marken-Layer ab
  // (Linear/Vercel/Stripe-Muster). gray-950 auf Weiß = 18,7:1.
  primary: cn(
    'bg-gray-950 text-white shadow-sm',
    'hover:bg-gray-800',
    'focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 focus-visible:outline-none',
  ),
  secondary: cn(
    'bg-white text-gray-900 border border-gray-300',
    'hover:border-gray-400 hover:bg-gray-50',
    'focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:outline-none',
  ),
  ghost: cn(
    'bg-transparent text-gray-900',
    'hover:bg-gray-100',
    'focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:outline-none',
  ),
  inverted: cn(
    'bg-white text-gray-950 shadow-sm',
    'hover:bg-gray-100',
    'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 focus-visible:outline-none',
  ),
};

const SIZE_CLASSES: Record<Size, string> = {
  md: cn(TOUCH.ctaSm, 'px-5 text-sm'),
  lg: cn(TOUCH.cta, 'px-7 text-base'),
};

function buildClasses(props: BaseProps) {
  return cn(
    'inline-flex items-center justify-center gap-2 font-semibold',
    RADII.pill,
    // Explizite Properties statt transition-all + Press-Feedback für Touch
    // (79% Mobile-Traffic): scale(0.97) im 150ms-Budget (Emil-Review)
    'transition-[background-color,border-color,color,transform] duration-150 ease-out',
    'active:scale-[0.97]',
    VARIANT_CLASSES[props.variant ?? 'primary'],
    SIZE_CLASSES[props.size ?? 'lg'],
    props.fullWidth && 'w-full',
    props.className,
  );
}

export function CTAButton(props: ButtonProps | LinkProps) {
  const cls = buildClasses(props);
  if ('href' in props && props.href) {
    const linkProps = props as LinkProps;
    if (linkProps.external) {
      return (
        <a
          href={linkProps.href}
          className={cls}
          aria-label={linkProps.ariaLabel}
          target="_blank"
          rel="noopener noreferrer"
          onClick={linkProps.onClick}
        >
          {linkProps.children}
        </a>
      );
    }
    return (
      <Link
        href={linkProps.href}
        className={cls}
        aria-label={linkProps.ariaLabel}
        onClick={linkProps.onClick}
      >
        {linkProps.children}
      </Link>
    );
  }
  const btn = props as ButtonProps;
  return (
    <button
      type={btn.type ?? 'button'}
      onClick={btn.onClick}
      disabled={btn.disabled}
      className={cn(cls, btn.disabled && 'cursor-not-allowed opacity-50')}
      aria-label={btn.ariaLabel}
    >
      {btn.children}
    </button>
  );
}
